import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { createTextColumn, date } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { renderNetworks } from 'app/features/DiscoverySettingsForms/components/DeviceCredentials/helpers/renderNetworks';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import type { RowDragState } from 'components/LazyRowContainer/LazyRowContainer';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import type { TableCellContext } from 'components/Table/model/TableCellContext';
import type { TableActionsItem } from 'components/Table/model/TableModel';
import { useDraggableTable } from 'components/Table/model/useDraggableTable';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useLocale } from 'containers/App/LocaleContext';
import { isAfter, startOfDay } from 'date-fns';
import { useCallback, useMemo } from 'react';
import type { FC, MouseEvent, ReactNode } from 'react';

export type EnablePassword = {
  /** list of CIDRs */
  excludeNetworks?: string[];
  expirationDate?: {
    enabled: boolean;
    // Null or undefined when enabled is false
    value?: string | null;
  };
  id: string;
  /** list of CIDRs */
  includeNetworks?: string[];
  notes?: string;
  password?: string;
  priority: number;
  username?: string;
  /**
   * Property telling backend to encrypt the submitted password. Populated automatically,
   * not used for an actual form field.
   */
  custom?: boolean;
};

type Props = {
  className?: string;
  passwords: EnablePassword[];
  onAddCredentialClick: () => void;
  onDeletePasswordClick: (id: string) => () => void;
  onDropPassword: (data: EnablePassword[], from: number, to: number) => void;
  onEditPasswordClick: (password: EnablePassword) => () => void;
  noCredentialBox?: ReactNode;
};

type EnablePasswordTableRow = EnablePassword & {
  allowedInSubnetsText: string;
  deniedInSubnetsText: string;
  expiresText: string;
  isExpired: boolean;
};

export const EnablePasswordsList: FC<Props> = (props) => {
  const credentialsMsg = useLocale().deviceCredentials.credentialsList;

  const globalCtxProps = useGlobalStateContextSelectors({
    dateFormat: prop('dateFormat'),
    timezone: prop('user', 'timezone'),
  });

  const tableData = useMemo<EnablePasswordTableRow[]>(
    () =>
      props.passwords.map((item) => {
        const isExpired = Boolean(
          item.expirationDate?.enabled &&
            item.expirationDate.value &&
            isAfter(
              startOfDay(new Date()),
              new Date(item.expirationDate.value),
            ),
        );
        const expiresText = isExpired
          ? 'Expired'
          : item.expirationDate?.enabled && item.expirationDate.value
            ? date.formatUtcTimestampInTimeZone(
                new Date(item.expirationDate.value),
                globalCtxProps.timezone,
                globalCtxProps.dateFormat,
              )
            : credentialsMsg.never;

        return {
          ...item,
          expiresText,
          isExpired,
          allowedInSubnetsText: item.includeNetworks?.join(', ') ?? '',
          deniedInSubnetsText: item.excludeNetworks?.join(', ') ?? '',
        };
      }),
    [
      props.passwords,
      globalCtxProps.timezone,
      globalCtxProps.dateFormat,
      credentialsMsg.never,
    ],
  );

  const draggable = useDraggableTable({
    data: props.passwords,
    onDrop: props.onDropPassword,
    getRowIdentity: (row) => row.id,
  });

  const columns = useMemo(
    () => [
      createFrontendOnlyColumn<EnablePasswordTableRow>({
        key: 'priority',
        title: '',
        disabledSort: true,
        fixedWidth: true,
        width: 40,
        render: (item) => (
          <Icon
            glyph='fa-grip-dots-vertical'
            size='sm'
            className='cursor-grab'
            onMouseDown={() => {
              const index = props.passwords.findIndex((p) => p.id === item.id);
              if (index >= 0) {
                draggable.onRowDragStart(index);
              }
            }}
          />
        ),
      }),
      createTextColumn({
        key: 'username',
        title: 'Name',
        renderContent: ({ row }: { row: EnablePasswordTableRow }) => ({
          className: 'flex items-center',
          content: (
            <div className='flex-1'>
              <div className='text-sm'>{row.username}</div>
              {row.notes && (
                <div className='text-xs text-gray-800 mt-1'>{row.notes}</div>
              )}
            </div>
          ),
        }),
      }),
      createTextColumn({
        key: 'expiresText',
        title: credentialsMsg.expires,
        renderContent: ({ row }: { row: EnablePasswordTableRow }) => ({
          content: (
            <div className='flex-1'>
              {row.isExpired ? (
                <Badge color='warning' variant='plain' className='h-5 px-1.5'>
                  Expired
                </Badge>
              ) : (
                <div className='text-sm'>{row.expiresText}</div>
              )}
            </div>
          ),
        }),
      }),
      createTextColumn({
        key: 'allowedInSubnetsText',
        title: credentialsMsg.allowedInSubnets,
        renderContent: ({
          row,
          context,
          index,
        }: {
          row: EnablePasswordTableRow;
          context: TableCellContext;
          index: number;
        }) => ({
          className: 'flex items-center',
          content: (
            <div className='flex flex-wrap gap-1 text-sm'>
              {!row.includeNetworks?.length
                ? credentialsMsg.noSubnets
                : renderNetworks(row.includeNetworks, {
                    showAll: context.detailView,
                    onShowMore: () =>
                      context.onShowDetailClick?.(
                        index,
                        'allowedInSubnetsText',
                      ),
                  })}
            </div>
          ),
        }),
      }),
      createTextColumn({
        key: 'deniedInSubnetsText',
        title: credentialsMsg.deniedInSubnets,
        renderContent: ({
          row,
          context,
          index,
        }: {
          row: EnablePasswordTableRow;
          context: TableCellContext;
          index: number;
        }) => ({
          className: 'flex items-center',
          content: (
            <div className='flex flex-wrap gap-1 text-sm'>
              {!row.excludeNetworks?.length
                ? credentialsMsg.noSubnets
                : renderNetworks(row.excludeNetworks, {
                    showAll: context.detailView,
                    onShowMore: () =>
                      context.onShowDetailClick?.(index, 'deniedInSubnetsText'),
                  })}
            </div>
          ),
        }),
      }),
      createFrontendOnlyColumn<EnablePasswordTableRow>({
        key: 'actions',
        title: 'Actions',
        disabledSort: true,
        render: (item) => (
          <div className='flex flex-row items-center gap-xs'>
            <TableButton
              dataTestId={`privileges-${item.username}-edit-icon`}
              variant='plain'
              icon='fa-pen'
              size='small'
              onClick={props.onEditPasswordClick(item)}
              type='button'
            />
            <TableButton
              dataTestId={`privileges-${item.username}-remove-icon`}
              variant='plain'
              accent='negative'
              icon='fa-trash-can'
              size='small'
              onClick={props.onDeletePasswordClick(item.id)}
              type='button'
            />
          </div>
        ),
      }),
    ],
    [
      credentialsMsg,
      props.onDeletePasswordClick,
      props.onEditPasswordClick,
      props.passwords,
      draggable.onRowDragStart,
    ],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: tableData,
  });
  const renderedRows = frontendSimpleFilter.data as EnablePasswordTableRow[];
  const sourceIndexById = useMemo(
    () =>
      new Map(props.passwords.map((row, index) => [row.id, index] as const)),
    [props.passwords],
  );
  const renderedIndexById = useMemo(
    () => new Map(renderedRows.map((row, index) => [row.id, index] as const)),
    [renderedRows],
  );

  const getRenderedDropBeforeIndex = useCallback(
    (dropBeforeIndex: number | null): number | null => {
      if (dropBeforeIndex === null) {
        return null;
      }
      if (dropBeforeIndex === props.passwords.length) {
        return renderedRows.length;
      }
      const beforeRowId = props.passwords[dropBeforeIndex]?.id;
      if (!beforeRowId) {
        return null;
      }
      return renderedIndexById.get(beforeRowId) ?? null;
    },
    [props.passwords, renderedRows.length, renderedIndexById],
  );

  const rowDragState = useMemo<RowDragState>(() => {
    const draggedRowId =
      draggable.rowDragState.draggedIndex === null
        ? null
        : (props.passwords[draggable.rowDragState.draggedIndex]?.id ?? null);
    return {
      draggedIndex:
        draggedRowId === null
          ? null
          : (renderedIndexById.get(draggedRowId) ?? null),
      dropBeforeIndex: getRenderedDropBeforeIndex(
        draggable.rowDragState.dropBeforeIndex,
      ),
    };
  }, [
    draggable.rowDragState.draggedIndex,
    draggable.rowDragState.dropBeforeIndex,
    getRenderedDropBeforeIndex,
    props.passwords,
    renderedIndexById,
  ]);

  const onRowMouseMove = useCallback(
    (renderedRowIndex: number, e: MouseEvent) => {
      const rowId = renderedRows[renderedRowIndex]?.id;
      if (!rowId) {
        return;
      }
      const sourceIndex = sourceIndexById.get(rowId);
      if (sourceIndex === undefined) {
        return;
      }
      draggable.onRowMouseMove(sourceIndex, e);
    },
    [draggable.onRowMouseMove, renderedRows, sourceIndexById],
  );

  const menuItems = useMemo(
    () => [
      {
        standalone: true,
        elementWithKey: (
          <Button
            dataTestId='enable-credentials-add-credential'
            icon='fa-circle-plus'
            key='add-new'
            onClick={props.onAddCredentialClick}
            size='small'
            variant='outline'
          >
            Add new
          </Button>
        ),
      } satisfies TableActionsItem,
    ],
    [props.onAddCredentialClick],
  );

  return (
    <Table
      className={props.className}
      borderless
      columns={columns}
      id='enablePasswordsNewDiscovery'
      menuItems={menuItems}
      rowIdField='id'
      rowHeight={70}
      noDataBox={props.noCredentialBox}
      {...frontendSimpleFilter}
      rowDragState={rowDragState}
      onRowMouseMove={onRowMouseMove}
      onRowMouseLeave={draggable.onRowMouseLeave}
      title='Enable mode passwords'
      description='Privileged credentials are generally necessary only for configuration
        backup, however some platforms require privileged credentials to access
        basic network state information, such as MST spanning tree state or
        802.1X session information.'
    />
  );
};
